import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, FileUp, ListChecks, Palette, PencilLine, ScanLine, ClipboardPaste, Target, Search, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import config from '@/seo.config.json';
import { ALL_TEMPLATES } from './resumeTemplates';

/**
 * The reference sections, built from the patterns the marketing site already uses.
 *
 * These had drifted into shapes that exist nowhere else we own — right-aligned serif
 * section titles, three-column rows of plain text. The site has a settled vocabulary
 * for exactly this material: a heading block with a coloured eyebrow, a hairline grid
 * of cards, selectable step cards, and a two-column accordion. Reusing it is what
 * makes the two pages read as one product.
 */

/** Eyebrow, display heading, body — the site's standard section opener. */
export function SectionHeading({
  eyebrow,
  title,
  body,
  inverted,
}: {
  eyebrow: string;
  title: string;
  body?: string;
  inverted?: boolean;
}) {
  return (
    <div className="max-w-2xl">
      <p
        className={cn(
          'mb-4 text-xs font-bold uppercase tracking-[0.18em]',
          inverted ? 'text-brand-soft' : 'text-brand'
        )}
      >
        {eyebrow}
      </p>
      <h2
        className={cn(
          'text-balance font-display text-3xl font-semibold leading-[1.08] tracking-tight sm:text-5xl',
          inverted ? 'text-white' : 'text-ink'
        )}
      >
        {title}
      </h2>
      {body && (
        <p
          className={cn(
            'mt-5 text-base leading-7 sm:text-lg',
            inverted ? 'text-white/60' : 'text-ink-muted'
          )}
        >
          {body}
        </p>
      )}
    </div>
  );
}

/**
 * The site's Metric: a small label over a display-face value.
 *
 * Used here for the four facts somebody weighs before uploading a CV to a stranger's
 * website. Deliberately not invented numbers — a landing page that claims "40,000
 * resumes scanned" when it has scanned none is the one thing that would make
 * everything else on the page worth less.
 */
/** Counted from the layouts themselves, so the number on the page cannot drift. */
export const FREE_LAYOUTS = ALL_TEMPLATES.filter((t) => !t.premium).length;

const FACTS: Array<[string, string]> = [
  ['Cost', 'Free'],
  ['Account', 'Not needed'],
  ['Stored', 'Only if you save'],
  ['A check takes', '~10s'],
  ['Free designs', String(FREE_LAYOUTS)],
  ['Write it in', 'Hindi or Hinglish'],
];

export function Facts() {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-5 border-y border-line py-6 sm:grid-cols-3 lg:grid-cols-6">
      {FACTS.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs text-ink-subtle">{label}</dt>
          <dd className="mt-1 font-display text-xl font-semibold text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const MEASURES = [
  {
    number: '01',
    Icon: Target,
    title: 'What the role asked for',
    body: 'Every requirement named in the advert, checked against your CV — and only counted as found if the words are genuinely there.',
  },
  {
    number: '02',
    Icon: Search,
    title: 'How the evidence reads',
    body: 'Whether a skill is named in passing, or shown in work you actually did, with the scale attached.',
  },
  {
    number: '03',
    Icon: ScanLine,
    title: 'Whether it parses',
    body: 'Layouts that read beautifully and extract badly are the quiet reason good applications disappear before anyone sees them.',
  },
];

export function WhatItMeasures() {
  return (
    <section aria-labelledby="measures-heading">
      <div id="measures-heading">
        <SectionHeading
          eyebrow="How the ATS score is calculated"
          title="What the ATS score actually measures."
          body="Three things, each checked against the job you pasted or picked, rather than against a general idea of a good CV."
        />
      </div>

      {/* gap-px over a line-coloured ground: the dividers are the gaps, so the cards
          sit in one block rather than floating as separate tiles. */}
      <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3">
        {MEASURES.map(({ number, Icon, title, body }) => (
          <div key={number} className="group bg-surface p-6 transition-colors hover:bg-brand-soft/30 sm:p-7">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-brand">{number}</span>
              <Icon className="size-5 text-ink-subtle transition-colors group-hover:text-brand" aria-hidden />
            </div>
            {/* The site sets this gap at mt-16, but its cards are five across and
                narrow, so the drop reads as a tall card. Three wide cards with two
                lines of body left a hole instead, so the proportion is kept rather
                than the number. */}
            <h3 className="mt-10 font-display text-lg font-semibold leading-snug text-ink">{title}</h3>
            <p className="mt-3 text-sm leading-6 text-ink-muted">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  {
    Icon: ClipboardPaste,
    eyebrow: 'STEP 01',
    label: 'Paste the job',
    title: 'The whole advert — requirements, responsibilities, all of it.',
    detail:
      'The score is against this one job, not a general rating of your CV. Paste more and the match gets sharper, because there is more for the scorer to check against.',
  },
  {
    Icon: FileUp,
    eyebrow: 'STEP 02',
    label: 'Add your CV',
    title: 'PDF, Word or plain text — or build one here.',
    detail:
      'It is read in memory to produce the score and then discarded. No CV yet? Answer a few questions in English, Hindi or Hinglish and we write one up for you, then check that.',
  },
  {
    Icon: ListChecks,
    eyebrow: 'STEP 03',
    label: 'Fix it, download it',
    title: 'A score out of 100, then the changes — yours to accept.',
    detail: `The requirements your CV never mentions and the changes that would move the number most, each on the line it belongs to. Accept the ones that are true, watch the score move, and download it in any of ${FREE_LAYOUTS} free designs as PDF or Word.`,
  },
];

export function HowItWorks({ inverted }: { inverted?: boolean }) {
  const [active, setActive] = useState(0);
  const step = STEPS[active]!;

  return (
    <section className="grid gap-14 lg:grid-cols-[.7fr_1.3fr] lg:items-start" aria-labelledby="how-heading">
      <div id="how-heading">
        <SectionHeading
          eyebrow="How the ATS checker works"
          title="Three things, about ten seconds."
          body="No account, no upload history, no email at the end of it."
          inverted={inverted}
        />
      </div>

      <div className="space-y-3">
        {/* Selectable cards rather than a static list: the detail panel below is the
            reason the three steps are worth more than one sentence each. */}
        <div className="grid gap-3 sm:grid-cols-3">
          {STEPS.map(({ Icon, eyebrow, label }, index) => (
            <button
              key={label}
              type="button"
              onClick={() => setActive(index)}
              aria-pressed={index === active}
              className={cn(
                'group rounded-xl border p-5 text-left transition-all',
                index === active
                  ? inverted
                    ? 'border-brand-soft bg-white/10'
                    : 'border-brand bg-brand-soft/40 shadow-sm'
                  : inverted
                    ? 'border-white/15 bg-white/[0.03] hover:border-brand-soft/50'
                    : 'border-line bg-surface hover:border-brand/40'
              )}
            >
              <div className="flex items-start justify-between">
                <span
                  className={cn(
                    'grid size-9 place-items-center rounded-lg transition-colors',
                    index === active
                      ? 'bg-brand text-white dark:text-ink-inverted'
                      : inverted
                        ? 'bg-white/10 text-white/60'
                        : 'bg-surface-raised text-ink-subtle'
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                </span>
                <span
                  className={cn(
                    'text-[10px] font-bold tracking-[0.14em]',
                    inverted ? 'text-white/60' : 'text-ink-subtle'
                  )}
                >
                  {eyebrow}
                </span>
              </div>
              <p className={cn('mt-7 text-sm font-semibold', inverted ? 'text-white' : 'text-ink')}>
                {label}
              </p>
            </button>
          ))}
        </div>

        <div
          className={cn(
            'rounded-xl border p-6',
            inverted ? 'border-white/15 bg-white/[0.03]' : 'border-line bg-surface'
          )}
        >
          <p className={cn('font-display text-xl font-semibold leading-snug', inverted ? 'text-white' : 'text-ink')}>
            {step.title}
          </p>
          <p className={cn('mt-3 max-w-xl text-sm leading-6', inverted ? 'text-white/60' : 'text-ink-muted')}>
            {step.detail}
          </p>
        </div>
      </div>
    </section>
  );
}

export interface FaqEntry {
  q: string;
  a: string;
}

/**
 * Questions and answers, one open at a time.
 *
 * The same entries go into the page's FAQPage markup at build time (from
 * seo.config.json), so what is marked up is exactly what is on the page.
 */
export function ResumeCheckFaq({
  entries = config.faq,
  eyebrow = 'Questions',
  title = 'Before you upload anything.',
  body = 'Mostly about what happens to your CV, which is the right thing to ask first.',
}: {
  entries?: FaqEntry[];
  eyebrow?: string;
  title?: string;
  body?: string;
}) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="grid gap-14 lg:grid-cols-[.7fr_1.3fr]" aria-labelledby="faq-heading">
      <div id="faq-heading">
        <SectionHeading eyebrow={eyebrow} title={title} body={body} />
      </div>

      <div className="divide-y divide-line border-y border-line">
        {entries.map((entry, index) => (
          <div key={entry.q}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-6 py-5 text-left"
              onClick={() => setOpen(open === index ? null : index)}
              aria-expanded={open === index}
            >
              <span className="text-sm font-semibold text-ink sm:text-base">{entry.q}</span>
              <ChevronDown
                className={cn('size-4 shrink-0 text-brand transition-transform', open === index && 'rotate-180')}
                aria-hidden
              />
            </button>
            {/* In the page even when closed, so the answer is in the HTML a crawler
                reads and matches the FAQ markup; closed is only hidden. */}
            <p hidden={open !== index} className="-mt-1 max-w-2xl pb-5 pr-8 text-sm leading-6 text-ink-muted">
              {entry.a}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Everything the page does besides the check, said once, with a glimpse of each.
 *
 * The check is the front door, but a large share of the people who arrive have no CV
 * yet, or have one that says the right things and looks wrong. Each of those gets a
 * picture of what it does — a Hinglish sentence becoming a CV line, a row of designs,
 * a line being fixed — rather than a feature name, because "AI resume builder" is a
 * phrase on every site and the sentence turning into a line is not.
 */
export function CvTools() {
  return (
    <section aria-labelledby="tools-heading">
      <div id="tools-heading">
        <SectionHeading
          eyebrow="One place for your CV"
          title="Check it, fix it, build it, dress it."
          body="Everything a CV needs before it goes out, free and with no account — and nothing you type is kept."
        />
      </div>

      <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-line bg-line lg:grid-cols-3">
        <Tool
          Icon={PencilLine}
          title="No CV yet? Build one the way you talk."
          body="Six short questions. Write what you did in English, Hindi or Hinglish — or just say it — and get proper CV lines back. Nothing you did not tell us goes in."
          to="/resume-builder"
          action="Build my CV"
        >
          <div className="space-y-2 text-[12.5px] leading-relaxed">
            <p className="rounded-lg rounded-bl-sm bg-surface-raised px-3 py-2 text-ink-muted">
              college ke liye attendance app banaya React se, 200 students ne use kiya
            </p>
            <p className="flex items-center gap-1.5 pl-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-brand">
              <Wand2 className="size-3" aria-hidden />
              becomes
            </p>
            <p className="rounded-lg rounded-br-sm border border-brand/30 bg-brand-soft/40 px-3 py-2 font-medium text-ink">
              • Built a React attendance app for college, used by 200 students
            </p>
          </div>
        </Tool>

        <Tool
          Icon={Palette}
          title="Happy with the words? Change the design."
          body={`Upload your CV and see it in every layout — ${FREE_LAYOUTS} free, with or without a photo, single column or two. Same words, set properly, as PDF or Word.`}
          to="/resume-check/restyle"
          action="Change the design"
        >
          <div aria-hidden className="flex items-end justify-center gap-2.5">
            <MiniSheet kind="plain" />
            <MiniSheet kind="sidebar" />
            <MiniSheet kind="photo" />
            <MiniSheet kind="banner" />
          </div>
        </Tool>

        <Tool
          Icon={ListChecks}
          title="Applying somewhere? Fix it for that job."
          body="Paste the advert and see what the screen will miss. Every change sits on the line it belongs to, shows how far it moves your score, and goes in only if you accept it."
          to="#check"
          action="Check my CV"
        >
          <div className="space-y-1.5 text-[12.5px] leading-relaxed">
            <p className="text-ink-subtle line-through decoration-danger/60">• Responsible for handling reports</p>
            <p className="flex items-start justify-between gap-2 font-medium text-ink">
              <span>• Built weekly sales reports in Power BI for 14 managers</span>
              <span className="shrink-0 rounded-full bg-positive-soft px-1.5 py-0.5 text-[10.5px] font-bold text-positive">+9</span>
            </p>
          </div>
        </Tool>
      </div>
    </section>
  );
}

function Tool({
  Icon,
  title,
  body,
  to,
  action,
  children,
}: {
  Icon: typeof Palette;
  title: string;
  body: string;
  to: string;
  action: string;
  children: React.ReactNode;
}) {
  const cta = (
    <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-brand">
      {action}
      <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
    </span>
  );
  return (
    <div className="group flex flex-col bg-surface p-6 transition-colors hover:bg-brand-soft/20 sm:p-7">
      <span className="grid size-10 place-items-center rounded-xl bg-brand-soft text-brand">
        <Icon className="size-5" aria-hidden />
      </span>
      <h3 className="mt-6 font-display text-lg font-semibold leading-snug text-ink">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-ink-muted">{body}</p>
      <div className="mt-6 flex min-h-[132px] flex-col justify-center rounded-xl border border-line bg-canvas p-4">{children}</div>
      {/* Pushed to the foot, so the three calls to action sit on one line whatever
          the copy above them runs to. */}
      <div className="mt-auto pt-6">
        {to.startsWith('#') ? (
          <a href={to} className="focus-visible:outline-brand">
            {cta}
          </a>
        ) : (
          <Link to={to} className="focus-visible:outline-brand">
            {cta}
          </Link>
        )}
      </div>
    </div>
  );
}

/** A thumbnail of a layout, drawn in a few strokes — enough to tell the designs apart. */
export function MiniSheet({ kind, large }: { kind: 'plain' | 'sidebar' | 'photo' | 'banner'; large?: boolean }) {
  const line = (w: string, strong = false) => (
    <span className={cn('block h-[3px] rounded-full', strong ? 'bg-[#333]' : 'bg-[#c9ccd1]')} style={{ width: w }} />
  );
  return (
    <span
      className={cn(
        'relative flex overflow-hidden rounded-[4px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.18)]',
        large ? 'aspect-[1/1.414] w-full' : 'h-[104px] w-[74px]'
      )}
    >
      {kind === 'sidebar' && <span className="w-[24px] shrink-0 bg-[#F4E9E4]" />}
      <span className="flex flex-1 flex-col gap-[5px] p-[7px]">
        {kind === 'banner' && <span className="-mx-[7px] -mt-[7px] mb-[2px] block h-[16px] bg-[#2F4A6D]" />}
        {kind === 'photo' ? (
          <span className="flex items-center gap-[5px]">
            <span className="size-[14px] shrink-0 rounded-full bg-[#9c5b4f]" />
            {line('60%', true)}
          </span>
        ) : kind !== 'banner' ? (
          <span className={cn('flex', kind === 'plain' && 'justify-center')}>{line('55%', true)}</span>
        ) : null}
        {line('35%', true)}
        {line('90%')}
        {line('80%')}
        {line('86%')}
        {line('35%', true)}
        {line('88%')}
        {line('70%')}
      </span>
    </span>
  );
}
